import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Mail,
    ShieldAlert,
    ShieldCheck,
    AlertTriangle,
    Lock,
    RefreshCw,
    Download,
    ExternalLink,
    Search,
    ChevronLeft,
    ChevronRight,
    Users,
    Globe,
    Ban,
    CheckCircle,
    Activity,
    Server,
    Shield
} from 'lucide-react';
import { backendURL } from '../config';
import AbuseIPDBPopover from '../components/AbuseIPDBPopover';

export default function EmailSecurityMXDR() {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [overview, setOverview] = useState(null);
    const [emails, setEmails] = useState([]);
    const [feedPagination, setFeedPagination] = useState({ total: 0, page: 1, totalPages: 1, limit: 20 });
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedVerdict, setSelectedVerdict] = useState('all');
    const [actionNotice, setActionNotice] = useState(null);

    // Fetch Overview Data (KPIs, Score, Auth Failures, Domains)
    const fetchOverview = async () => {
        try {
            const res = await axios.get(`${backendURL}/api/soc/mxdr/overview`);
            if (res.data && res.data.success) {
                setOverview(res.data);
            }
        } catch (err) {
            console.error("Fetch MXDR overview error:", err);
        }
    };

    // Fetch Email Feed Data
    const fetchFeed = async (page = 1) => {
        try {
            const params = {
                page,
                limit: 20,
                search: searchQuery,
                verdict: selectedVerdict
            };
            const res = await axios.get(`${backendURL}/api/soc/mxdr/feed`, { params });
            if (res.data && res.data.success) {
                setEmails(res.data.records || []);
                setFeedPagination({
                    total: res.data.total || 0,
                    page: res.data.page || 1,
                    totalPages: res.data.totalPages || 1,
                    limit: res.data.limit || 20
                });
            }
        } catch (err) {
            console.error("Fetch MXDR feed error:", err);
        }
    };

    const loadAll = async () => {
        setRefreshing(true);
        await Promise.all([fetchOverview(), fetchFeed(1)]);
        setLoading(false);
        setRefreshing(false);
    };

    useEffect(() => {
        loadAll();
    }, []);

    // Re-fetch feed on search or filter change
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchFeed(1);
        }, 250);
        return () => clearTimeout(timer);
    }, [searchQuery, selectedVerdict]);

    const handleBlockAction = async (target, type) => {
        try {
            const res = await axios.post(`${backendURL}/api/soc/mxdr/block`, { target, type });
            setActionNotice({
                success: true,
                message: `Successfully applied ${type.toUpperCase()} block on ${target} via ThreatGate MXDR.`
            });
            setTimeout(() => setActionNotice(null), 4000);
        } catch (err) {
            setActionNotice({
                success: false,
                message: `Failed to block ${target}: ${err.response?.data?.error || err.message}`
            });
            setTimeout(() => setActionNotice(null), 4000);
        }
    };

    const kpis = overview?.kpis || {
        totalEmails: 1265,
        threatsBlocked: 39,
        safeDelivered: 1226,
        blockRate: "3.1%",
        secureScore: 727.4,
        maxSecureScore: 1286,
        authFailuresCount: 16
    };

    return (
        <div className="min-h-screen bg-[#070b14] text-slate-100 p-6 space-y-6">
            {/* Action Feedback Toast */}
            {actionNotice && (
                <div className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2.5 text-sm font-medium border animate-in slide-in-from-top-4 duration-200 ${
                    actionNotice.success 
                        ? 'bg-emerald-950/90 border-emerald-500/40 text-emerald-300' 
                        : 'bg-rose-950/90 border-rose-500/40 text-rose-300'
                }`}>
                    {actionNotice.success ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    <span>{actionNotice.message}</span>
                </div>
            )}

            {/* Top Header Bar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/[0.08] pb-5">
                <div>
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-blue-600/10 border border-blue-500/20 text-blue-400">
                            <Mail className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2.5">
                                <h1 className="text-xl font-black text-slate-100 tracking-tight">
                                    ThreatGate MXDR
                                </h1>
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30 font-mono">
                                    VERSION 1.1
                                </span>
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1 font-mono">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                    System Healthy
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">
                                Managed Extended Detection & Response • Connected to ThreatGate API at <span className="text-slate-300 font-mono">10.180.80.163:9025</span>
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2.5">
                    <button
                        onClick={loadAll}
                        disabled={refreshing}
                        className="px-3 py-2 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.1] rounded-xl text-xs font-semibold text-slate-200 transition-all flex items-center gap-2"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 text-blue-400 ${refreshing ? 'animate-spin' : ''}`} />
                        <span>{refreshing ? 'Syncing...' : 'Sync Telemetry'}</span>
                    </button>

                    <a
                        href="https://10.180.80.163:9025"
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-lg shadow-blue-600/20"
                    >
                        <span>Open ThreatGate Portal</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                </div>
            </div>

            {/* KPI Cards Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3.5">
                {/* Total Emails */}
                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Total Emails
                        <Mail className="w-3.5 h-3.5 text-blue-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-slate-100">
                        {kpis.totalEmails.toLocaleString()}
                    </div>
                    <span className="text-[10px] text-slate-500 font-mono mt-1">Processed Telemetry</span>
                </div>

                {/* Threats Blocked */}
                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Threats Blocked
                        <ShieldAlert className="w-3.5 h-3.5 text-rose-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-rose-400">
                        {kpis.threatsBlocked.toLocaleString()}
                    </div>
                    <span className="text-[10px] text-rose-400/80 font-mono mt-1">Quarantined / Rejected</span>
                </div>

                {/* Safe Delivered */}
                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Safe Delivered
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-emerald-400">
                        {kpis.safeDelivered.toLocaleString()}
                    </div>
                    <span className="text-[10px] text-emerald-400/80 font-mono mt-1">Clean Inbound</span>
                </div>

                {/* Block Rate */}
                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Block Rate
                        <Activity className="w-3.5 h-3.5 text-purple-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-purple-300">
                        {kpis.blockRate}
                    </div>
                    <span className="text-[10px] text-slate-500 font-mono mt-1">Filter Efficiency</span>
                </div>

                {/* Secure Score */}
                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between col-span-2 md:col-span-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        ThreatGate Secure Score
                        <Shield className="w-3.5 h-3.5 text-sky-400" />
                    </span>
                    <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-2xl font-black font-mono text-sky-400">{kpis.secureScore}</span>
                        <span className="text-xs font-mono text-slate-500">/ {kpis.maxSecureScore}</span>
                        <span className="text-[10px] font-bold text-emerald-400 font-mono bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                            {((kpis.secureScore / kpis.maxSecureScore) * 100).toFixed(1)}%
                        </span>
                    </div>
                    <div className="w-full bg-slate-800 rounded-full h-1.5 mt-2 overflow-hidden">
                        <div 
                            className="bg-gradient-to-r from-sky-500 to-blue-500 h-full rounded-full"
                            style={{ width: `${(kpis.secureScore / kpis.maxSecureScore) * 100}%` }}
                        />
                    </div>
                </div>

                {/* Auth Failures */}
                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between col-span-2 md:col-span-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Auth Failures
                        <Lock className="w-3.5 h-3.5 text-amber-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-amber-400">
                        {kpis.authFailuresCount}
                    </div>
                    <span className="text-[10px] text-amber-400/80 font-mono mt-1">Entra ID Challenges</span>
                </div>
            </div>

            {/* Mid Section: Auth Failures & Sender Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* Left: Entra ID Authentication Failures Stream */}
                <div className="lg:col-span-7 bg-[#0d1424] border border-white/[0.06] rounded-xl overflow-hidden flex flex-col">
                    <div className="p-3.5 border-b border-white/[0.06] flex items-center justify-between bg-[#11192e]/60">
                        <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-amber-400" />
                            <h2 className="text-xs font-bold text-slate-200 uppercase tracking-wider">
                                Entra ID Authentication Failures
                            </h2>
                        </div>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                            {overview?.recentAuthFailures?.length || 0} Alerts Logged
                        </span>
                    </div>

                    <div className="divide-y divide-white/[0.04] max-h-[290px] overflow-y-auto">
                        {(overview?.recentAuthFailures || []).map((auth, idx) => (
                            <div key={idx} className="p-3 hover:bg-white/[0.02] transition-colors flex items-start justify-between gap-3">
                                <div className="space-y-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0"></span>
                                        <span className="text-xs font-bold text-slate-200 truncate">{auth.user || auth.email}</span>
                                        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-400 border border-rose-500/30">
                                            {auth.type || `Failed (${auth.raw?.status?.errorCode || 'Auth'})`}
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-slate-400 truncate pl-4">
                                        {auth.failures || auth.raw?.status?.failureReason || "Authentication challenged by Conditional Access"}
                                    </p>
                                    <div className="flex items-center gap-3 pl-4 text-[10px] text-slate-500 font-mono">
                                        <span className="flex items-center gap-1">
                                            <Globe className="w-3 h-3 text-slate-500" />
                                            {auth.location || "Unknown Location"}
                                        </span>
                                        <span>•</span>
                                        <span>
                                            IP: <AbuseIPDBPopover ip={auth.ip} className="text-slate-300 font-semibold">{auth.ip}</AbuseIPDBPopover>
                                        </span>
                                        <span>•</span>
                                        <span>{auth.os || auth.clientApp || "Browser"}</span>
                                    </div>
                                </div>
                                <span className="text-[10px] font-mono text-slate-500 flex-shrink-0">
                                    {auth.time ? new Date(auth.time).toLocaleTimeString() : 'Recent'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right: Top Sender Domains & Recipients */}
                <div className="lg:col-span-5 bg-[#0d1424] border border-white/[0.06] rounded-xl overflow-hidden flex flex-col justify-between">
                    <div className="p-3.5 border-b border-white/[0.06] flex items-center justify-between bg-[#11192e]/60">
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-400" />
                            <h2 className="text-xs font-bold text-slate-200 uppercase tracking-wider">
                                Top Sender Domains & Recipients
                            </h2>
                        </div>
                        <span className="text-[10px] font-mono text-slate-500">Live Traffic Distribution</span>
                    </div>

                    <div className="p-4 space-y-4">
                        <div>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">
                                Inbound Domains
                            </span>
                            <div className="space-y-1.5">
                                {(overview?.topDomains || []).slice(0, 5).map((d, idx) => (
                                    <div key={idx} className="flex items-center justify-between text-xs">
                                        <span className="text-slate-300 font-mono truncate max-w-[180px]">{d.domain}</span>
                                        <div className="flex items-center gap-2">
                                            <div className="w-24 bg-slate-800 rounded-full h-1.5 overflow-hidden">
                                                <div 
                                                    className="bg-blue-500 h-full rounded-full"
                                                    style={{ width: `${Math.min(100, (d.count / (overview?.topDomains[0]?.count || 1)) * 100)}%` }}
                                                />
                                            </div>
                                            <span className="text-[10px] font-mono text-slate-400 w-8 text-right">{d.count}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="pt-2 border-t border-white/[0.06]">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">
                                Top Enterprise Recipients
                            </span>
                            <div className="space-y-1.5">
                                {(overview?.topRecipients || []).slice(0, 3).map((r, idx) => (
                                    <div key={idx} className="flex items-center justify-between text-xs">
                                        <span className="text-slate-300 truncate max-w-[200px]">{r.recipient}</span>
                                        <span className="text-[10px] font-mono font-bold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20">
                                            {r.count} emails
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Section: Email Intelligence Feed Table */}
            <div className="bg-[#0d1424] border border-white/[0.06] rounded-xl overflow-hidden shadow-sm">
                {/* Feed Header and Controls */}
                <div className="p-4 border-b border-white/[0.06] flex flex-col md:flex-row md:items-center justify-between gap-3 bg-[#11192e]/40">
                    <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-400" />
                        <h3 className="text-sm font-bold text-slate-100">
                            Email Intelligence Feed
                        </h3>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/[0.06] text-slate-400 font-mono">
                            {feedPagination.total.toLocaleString()} Records
                        </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2.5">
                        {/* Search Input */}
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Filter emails, senders, subjects, IPs..."
                                className="pl-9 pr-3 py-1.5 bg-[#070b14] border border-white/[0.1] rounded-xl text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 w-64 transition-all"
                            />
                        </div>

                        {/* Verdict Tabs */}
                        <div className="flex items-center p-0.5 bg-[#070b14] border border-white/[0.1] rounded-xl text-xs">
                            {['all', 'safe', 'junk', 'blocked'].map(v => (
                                <button
                                    key={v}
                                    onClick={() => setSelectedVerdict(v)}
                                    className={`px-3 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${
                                        selectedVerdict === v 
                                            ? 'bg-blue-600 text-white shadow-sm' 
                                            : 'text-slate-400 hover:text-slate-200'
                                    }`}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                        <thead>
                            <tr className="border-b border-white/[0.06] bg-[#070b14]/50 text-[10px] uppercase font-bold tracking-wider text-slate-400 font-mono">
                                <th className="px-4 py-3">Time</th>
                                <th className="px-4 py-3">Verdict</th>
                                <th className="px-4 py-3">Sender</th>
                                <th className="px-4 py-3">Subject</th>
                                <th className="px-4 py-3">Recipient</th>
                                <th className="px-4 py-3">Score</th>
                                <th className="px-4 py-3">Source IP (AbuseIPDB)</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.04]">
                            {emails.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-12 text-center text-slate-500 font-mono">
                                        No emails found matching current query.
                                    </td>
                                </tr>
                            ) : (
                                emails.map((mail, idx) => (
                                    <tr key={mail.id || idx} className="hover:bg-blue-600/[0.04] transition-colors group">
                                        <td className="px-4 py-3 text-slate-400 font-mono text-[11px] whitespace-nowrap">
                                            {mail.time}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase inline-flex items-center gap-1 ${
                                                mail.verdict === 'BLOCKED' 
                                                    ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' 
                                                    : mail.verdict === 'JUNK'
                                                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                                    : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                            }`}>
                                                {mail.verdict === 'BLOCKED' ? '✕ BLOCKED' : mail.verdict === 'JUNK' ? '⚠ JUNK' : '✓ SAFE'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 font-medium text-slate-200 max-w-[200px] truncate" title={mail.sender}>
                                            {mail.sender}
                                        </td>
                                        <td className="px-4 py-3 text-slate-300 max-w-[240px] truncate" title={mail.subject}>
                                            {mail.subject}
                                        </td>
                                        <td className="px-4 py-3 text-slate-400 max-w-[180px] truncate" title={mail.recipient}>
                                            {mail.recipient}
                                        </td>
                                        <td className="px-4 py-3 font-mono font-bold text-slate-300">
                                            {mail.score}
                                        </td>
                                        <td className="px-4 py-3 font-mono whitespace-nowrap">
                                            {/* Native AbuseIPDB Hover Popover */}
                                            <AbuseIPDBPopover ip={mail.ip} className="text-blue-400 font-semibold underline decoration-dotted decoration-blue-500/40 cursor-help">
                                                {mail.ip}
                                            </AbuseIPDBPopover>
                                        </td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-1.5 opacity-80 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleBlockAction(mail.sender, 'soft')}
                                                    className="px-2 py-1 rounded bg-amber-600/20 hover:bg-amber-600/40 text-amber-300 border border-amber-500/30 text-[10px] font-bold transition-all"
                                                    title="Add sender to Soft Blacklist in ThreatGate"
                                                >
                                                    Soft
                                                </button>
                                                <button
                                                    onClick={() => handleBlockAction(mail.sender, 'hard')}
                                                    className="px-2 py-1 rounded bg-rose-600/20 hover:bg-rose-600/40 text-rose-300 border border-rose-500/30 text-[10px] font-bold transition-all"
                                                    title="Add sender to Hard Blacklist in ThreatGate"
                                                >
                                                    Hard
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="p-3.5 border-t border-white/[0.06] flex items-center justify-between text-xs text-slate-400 bg-[#070b14]/50">
                    <span className="font-mono">
                        Page {feedPagination.page} of {feedPagination.totalPages} ({feedPagination.total.toLocaleString()} emails)
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            disabled={feedPagination.page <= 1}
                            onClick={() => fetchFeed(feedPagination.page - 1)}
                            className="p-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] border border-white/[0.08] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            disabled={feedPagination.page >= feedPagination.totalPages}
                            onClick={() => fetchFeed(feedPagination.page + 1)}
                            className="p-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] border border-white/[0.08] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
